import React from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import Croppie from 'croppie';
import 'croppie/croppie.css';

/**
 * SignatureCroppieModal
 *
 * Props:
 * - visible: boolean
 * - onClose: () => void
 * - onSave: (blob: Blob) => void
 * - viewportWidth?: number  (default 700)
 * - viewportHeight?: number (default 150)
 * - format?: 'png' | 'jpeg' (default 'png')
 * - pngTransparent?: boolean (default true → png mit Alpha)
 */

const SignatureCroppieModal = ({
  visible,
  onClose,
  onSave,
  viewportWidth = 700,
  viewportHeight = 150,
  format = 'png',
  pngTransparent = true
}) => {
  const wrapperRef = React.useRef(null);
  const croppieRef = React.useRef(null);
  const [hasImage, setHasImage] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  // File <input/> Ref (für erneutes Auswählen)
  const fileInputRef = React.useRef(null);

  // Croppie initialisieren / zerstören
  React.useEffect(() => {
    if (visible && wrapperRef.current && !croppieRef.current) {
      croppieRef.current = new Croppie(wrapperRef.current, {
        enableExif: true,
        enableOrientation: true,
        enforceBoundary: true,
        enableZoom: true,          // (Default zwar true, aber hier explizit)
        showZoomer: true,          // <-- WICHTIG: Root-Level, nicht in viewport!
        mouseWheelZoom: 'ctrl',    // Zoom per Mausrad nur mit STRG gedrückt
        viewport: {
          width: viewportWidth,
          height: viewportHeight,
          type: 'square'           // rechteckiger Ausschnitt
        },
        boundary: {
          width: Math.round(viewportWidth * 1.25),
          height: Math.round(viewportHeight * 1.15)
        }
      });
    }
    return () => {
      if (croppieRef.current) {
        croppieRef.current.destroy();
        croppieRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, viewportWidth, viewportHeight]);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      if (croppieRef.current) {
        croppieRef.current.bind({
          url: e.target.result
          // Optional: zoom: 1
        }).then(() => {
          setHasImage(true);
        });
      }
    };
    reader.readAsDataURL(file);
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    handleFile(file);
  };

  const rotateLeft = () => croppieRef.current && croppieRef.current.rotate(-90);
  const rotateRight = () => croppieRef.current && croppieRef.current.rotate(90);

  const handleSave = async () => {
    if (!croppieRef.current || !hasImage) return;
    setBusy(true);
    try {
      const result = await croppieRef.current.result({
        type: 'blob',
        size: { width: viewportWidth, height: viewportHeight },
        format: format,                       // 'png' oder 'jpeg'
        quality: format === 'jpeg' ? 0.92 : 1,
        circle: false,
        backgroundColor: pngTransparent ? undefined : '#FFFFFF'
      });
      onSave(result); // Blob an Parent
    } catch (e) {
      console.error('Croppie result error:', e);
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    setHasImage(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose && onClose();
  };

  if (!visible) return null;

  return (
    <Dialog title="Signatur zuschneiden" onClose={handleClose} width={Math.max(720, viewportWidth + 150)}>
      <div style={{ padding: 20 }}>
        {/* File-Upload */}
        <div style={{ marginBottom: 12 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={onFileChange}
          />
        </div>

        {/* Croppie-Wrapper */}
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div
            ref={wrapperRef}
            style={{
              border: '1px solid #ddd',
              borderRadius: 8,
              // kein overflow hidden, sonst verschwindet der Zoomer
              width: Math.round(viewportWidth * 1.25),
              // etwas extra Platz für den Slider (ca. +40–50px)
              height: Math.round(viewportHeight * 1.25) + 50
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <Button onClick={rotateLeft} disabled={!hasImage}>↶ Drehen</Button>
            <Button onClick={rotateRight} disabled={!hasImage}>↷ Drehen</Button>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16, marginRight: 20 }}>
          <Button onClick={handleClose} disabled={busy}>Abbrechen</Button>
          <Button themeColor="primary" onClick={handleSave} disabled={!hasImage || busy}>
            {busy ? 'Speichern…' : 'Speichern'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default SignatureCroppieModal;
